import type {
  Candle,
  DetectedSetup,
  IndicatorSeries,
  RangeState,
  SessionKey,
  SwingPoint,
  Timeframe
} from './types.ts';

const buildTargets = (entry: number, stop: number, direction: 'LONG' | 'SHORT') => {
  const risk = Math.max(Math.abs(entry - stop), Number.EPSILON);
  const multiplier = direction === 'LONG' ? 1 : -1;
  return [
    { level: entry + multiplier * risk * 2, label: 'Target 1 (2R)' },
    { level: entry + multiplier * risk * 3, label: 'Target 2 (3R)' }
  ];
};

const scoreConfidence = (points: number, strictMode: boolean) => {
  const high = strictMode ? 85 : 75;
  const med = strictMode ? 65 : 50;
  if (points >= high) return 'HIGH';
  if (points >= med) return 'MED';
  return 'LOW';
};

const buildChecklist = (items: Array<{ key: string; label: string; pass: boolean }>) => items;

const last = <T,>(arr: T[]) => (arr.length ? arr[arr.length - 1] : undefined);

const findRecentSwing = (swings: SwingPoint[], type: 'HIGH' | 'LOW') =>
  [...swings].reverse().find((s) => s.type === type);

const computeRR = (entry: number, stop: number, target: number) => {
  const risk = Math.abs(entry - stop);
  const reward = Math.abs(target - entry);
  return risk > 0 ? reward / risk : 0;
};

const findPullbackStartIndex = (
  candles: Candle[],
  indicators: IndicatorSeries,
  startIndex: number,
  direction: 'LONG' | 'SHORT'
) => {
  const isLong = direction === 'LONG';
  let i = Math.max(startIndex, 0);
  while (i >= 0) {
    const ema9 = indicators.ema9[i];
    const ema20 = indicators.ema20[i];
    if (!ema9 || !ema20) break;
    const close = candles[i].c;
    const pullbackOk = isLong ? close < ema9 && close >= ema20 : close > ema9 && close <= ema20;
    if (!pullbackOk) break;
    i -= 1;
  }
  return Math.min(startIndex, i + 1);
};

const findVwapReclaimStartIndex = (
  candles: Candle[],
  vwapSeries: Array<number | null>,
  startIndex: number
) => {
  let i = Math.max(startIndex, 0);
  while (i >= 0) {
    const vwap = vwapSeries[i];
    if (!vwap) break;
    if (candles[i].c < vwap) {
      i -= 1;
      continue;
    }
    break;
  }
  return Math.min(startIndex, i + 1);
};

const findExtensionStartIndex = (
  candles: Candle[],
  indicators: IndicatorSeries,
  startIndex: number,
  atrMultiplier = 2
) => {
  let i = Math.max(startIndex, 0);
  let first = i;
  while (i >= 0) {
    const ema20 = indicators.ema20[i];
    const atr = indicators.atr14[i];
    if (!ema20 || !atr) break;
    const extension = Math.abs(candles[i].c - ema20) > atr * atrMultiplier;
    if (!extension) break;
    first = i;
    i -= 1;
  }
  return first;
};

const baseChecklist = (rrPass: boolean, hasVolume: boolean) =>
  buildChecklist([
    { key: 'regime', label: 'Regime match', pass: true },
    { key: 'rr', label: 'R:R ≥ 2', pass: rrPass },
    { key: 'volume', label: 'Volume present', pass: hasVolume },
    { key: 'news', label: 'No news filter', pass: true }
  ]);

export const detectEmaPullback = (params: {
  symbol: string;
  timeframe: Timeframe;
  candles: Candle[];
  indicators: IndicatorSeries;
  swings: SwingPoint[];
  trend: 'UP' | 'DOWN' | 'RANGE' | 'UNKNOWN';
  strictMode: boolean;
  hasVolume: boolean;
}): DetectedSetup[] => {
  const { candles, indicators, trend, swings, strictMode, symbol, timeframe, hasVolume } = params;
  const idx = candles.length - 1;
  if (idx < 2) return [];
  const ema9 = indicators.ema9[idx];
  const ema20 = indicators.ema20[idx];
  if (!ema9 || !ema20) return [];
  const prevClose = candles[idx - 1].c;
  const currClose = candles[idx].c;

  const setups: DetectedSetup[] = [];

  const checkSide = (direction: 'LONG' | 'SHORT') => {
    const isLong = direction === 'LONG';
    const regimeMatch = isLong ? trend === 'UP' : trend === 'DOWN';
    if (!regimeMatch) return;
    const pullbackOk = isLong
      ? currClose < ema9 && currClose >= ema20
      : currClose > ema9 && currClose <= ema20;
    const trigger = isLong
      ? prevClose <= ema9 && currClose > ema9
      : prevClose >= ema9 && currClose < ema9;
    if (!pullbackOk && !trigger) return;

    const swing = findRecentSwing(swings, isLong ? 'LOW' : 'HIGH');
    const stopLevel = swing?.price ?? (isLong ? candles[idx].l : candles[idx].h);
    const entryLevel = currClose;
    const targets = buildTargets(entryLevel, stopLevel, direction);
    const rr = computeRR(entryLevel, stopLevel, targets[0].level);
    const points = (regimeMatch ? 40 : 0) + (trigger ? 30 : 15) + (rr >= 2 ? 20 : 0) + (hasVolume ? 10 : 0);
    const confidence = scoreConfidence(points, strictMode);
    const pullbackStartIndex = findPullbackStartIndex(
      candles,
      indicators,
      trigger ? idx - 1 : idx,
      direction
    );
    setups.push({
      id: `ema-pullback-${direction.toLowerCase()}-${candles[idx].t}`,
      symbol,
      timeframe,
      setup: 'EMA_PULLBACK',
      direction,
      status: trigger ? 'TRIGGERED' : 'ACTIVE',
      detectedAt: candles[idx].t,
      patternStartAt: candles[Math.max(pullbackStartIndex, 0)]?.t ?? candles[idx].t,
      entry: {
        type: 'MARKET_ON_TRIGGER',
        level: entryLevel,
        conditionText: isLong ? 'Close back above EMA9' : 'Close back below EMA9'
      },
      stop: {
        level: stopLevel,
        reason: 'Pullback swing invalidation'
      },
      targets,
      confidence,
      why: [
        `EMA9 ${isLong ? 'above' : 'below'} EMA20 with trend structure`,
        trigger ? 'Trigger candle reclaimed EMA9' : 'Pullback holding EMA20'
      ],
      checklist: baseChecklist(rr >= 2, hasVolume),
      draw: [
        { kind: 'line', id: 'entry', price: entryLevel, label: 'Entry' },
        { kind: 'line', id: 'stop', price: stopLevel, label: 'Stop' },
        { kind: 'line', id: 'target1', price: targets[0].level, label: targets[0].label }
      ]
    });
  };

  checkSide('LONG');
  checkSide('SHORT');

  return setups;
};

export const detectRangeFade = (params: {
  symbol: string;
  timeframe: Timeframe;
  candles: Candle[];
  range: RangeState | null;
  strictMode: boolean;
  hasVolume: boolean;
}): DetectedSetup[] => {
  const { range, candles, symbol, timeframe, strictMode, hasVolume } = params;
  if (!range) return [];
  const idx = candles.length - 1;
  const candle = candles[idx];
  const rangeHigh = range.high;
  const rangeLow = range.low;
  const upperReject = candle.h > rangeHigh && candle.c < rangeHigh;
  const lowerReject = candle.l < rangeLow && candle.c > rangeLow;
  const setups: DetectedSetup[] = [];

  const build = (direction: 'LONG' | 'SHORT') => {
    const isLong = direction === 'LONG';
    const entryLevel = candle.c;
    const stopLevel = isLong ? rangeLow - (rangeHigh - rangeLow) * 0.2 : rangeHigh + (rangeHigh - rangeLow) * 0.2;
    const targets = [
      { level: (rangeHigh + rangeLow) / 2, label: 'Midpoint' },
      { level: isLong ? rangeHigh : rangeLow, label: 'Opposite Range' }
    ];
    const rr = computeRR(entryLevel, stopLevel, targets[1].level);
    const points = (rr >= 2 ? 40 : 20) + (hasVolume ? 10 : 0) + 30;
    const confidence = scoreConfidence(points, strictMode);
    setups.push({
      id: `range-fade-${direction.toLowerCase()}-${candle.t}`,
      symbol,
      timeframe,
      setup: 'RANGE_FADE',
      direction,
      status: 'ACTIVE',
      detectedAt: candle.t,
      patternStartAt: candles[range.startIndex]?.t ?? candle.t,
      entry: {
        type: 'MARKET_ON_TRIGGER',
        level: entryLevel,
        conditionText: isLong ? 'Rejection off range low' : 'Rejection off range high'
      },
      stop: {
        level: stopLevel,
        reason: 'Range break invalidation'
      },
      targets,
      confidence,
      why: ['Range compression detected', 'Rejection wick at range boundary'],
      checklist: baseChecklist(rr >= 2, hasVolume),
      draw: [
        { kind: 'box', id: 'range', fromIndex: range.startIndex, toIndex: range.endIndex, high: range.high, low: range.low, label: 'Range' },
        { kind: 'line', id: 'entry', price: entryLevel, label: 'Entry' },
        { kind: 'line', id: 'stop', price: stopLevel, label: 'Stop' }
      ]
    });
  };

  if (lowerReject) build('LONG');
  if (upperReject) build('SHORT');

  return setups;
};

export const detectVwapReclaim = (params: {
  symbol: string;
  timeframe: Timeframe;
  candles: Candle[];
  indicators: IndicatorSeries;
  session: SessionKey;
  strictMode: boolean;
  hasVolume: boolean;
}): DetectedSetup[] => {
  const { candles, indicators, symbol, timeframe, strictMode, hasVolume } = params;
  const idx = candles.length - 1;
  if (idx < 2) return [];
  const vwap = indicators.vwap[idx];
  const vwapPrev = indicators.vwap[idx - 1];
  if (!vwap || !vwapPrev) return [];
  const prevClose = candles[idx - 1].c;
  const currClose = candles[idx].c;
  const slopeUp = vwap > vwapPrev;
  const reclaim = prevClose < vwap && currClose > vwap && slopeUp;
  if (!reclaim) return [];

  const stopLevel = Math.min(candles[idx - 1].l, candles[idx].l);
  const entryLevel = currClose;
  const targets = buildTargets(entryLevel, stopLevel, 'LONG');
  const rr = computeRR(entryLevel, stopLevel, targets[0].level);
  const points = 30 + (rr >= 2 ? 30 : 10) + (hasVolume ? 20 : 0);
  const confidence = scoreConfidence(points, strictMode);

  const reclaimStartIndex = findVwapReclaimStartIndex(candles, indicators.vwap, idx - 1);
  return [
    {
      id: `vwap-reclaim-${candles[idx].t}`,
      symbol,
      timeframe,
      setup: 'VWAP_RECLAIM',
      direction: 'LONG',
      status: 'TRIGGERED',
      detectedAt: candles[idx].t,
      patternStartAt: candles[reclaimStartIndex]?.t ?? candles[idx].t,
      entry: { type: 'MARKET_ON_TRIGGER', level: entryLevel, conditionText: 'Reclaim VWAP' },
      stop: { level: stopLevel, reason: 'Close back below VWAP' },
      targets,
      confidence: hasVolume ? confidence : confidence === 'HIGH' ? 'MED' : confidence,
      why: ['Price reclaimed VWAP with positive slope'],
      checklist: baseChecklist(rr >= 2, hasVolume),
      draw: [
        { kind: 'line', id: 'entry', price: entryLevel, label: 'Entry' },
        { kind: 'line', id: 'stop', price: stopLevel, label: 'Stop' },
        { kind: 'line', id: 'target1', price: targets[0].level, label: targets[0].label }
      ]
    }
  ];
};

export const detectOrb = (params: {
  symbol: string;
  timeframe: Timeframe;
  candles: Candle[];
  sessionStartIndex: number | null;
  orbWindowBars: number;
  strictMode: boolean;
  hasVolume: boolean;
}): DetectedSetup[] => {
  const { candles, sessionStartIndex, orbWindowBars, symbol, timeframe, strictMode, hasVolume } = params;
  if (sessionStartIndex === null) return [];
  const idx = candles.length - 1;
  const windowEnd = sessionStartIndex + orbWindowBars - 1;
  if (idx <= windowEnd) return [];
  const orbSlice = candles.slice(sessionStartIndex, windowEnd + 1);
  const orbHigh = Math.max(...orbSlice.map((c) => c.h));
  const orbLow = Math.min(...orbSlice.map((c) => c.l));
  const currClose = candles[idx].c;
  if (currClose > orbHigh) {
    const stopLevel = orbLow;
    const targets = buildTargets(currClose, stopLevel, 'LONG');
    const rr = computeRR(currClose, stopLevel, targets[0].level);
    const points = 40 + (rr >= 2 ? 20 : 10) + (hasVolume ? 10 : 0);
    return [
      {
        id: `orb-long-${candles[idx].t}`,
        symbol,
        timeframe,
        setup: 'ORB',
        direction: 'LONG',
        status: 'TRIGGERED',
        detectedAt: candles[idx].t,
        patternStartAt: candles[Math.max(sessionStartIndex, 0)]?.t ?? candles[idx].t,
        entry: { type: 'MARKET_ON_TRIGGER', level: currClose, conditionText: 'Close above OR high' },
        stop: { level: stopLevel, reason: 'Back inside OR' },
        targets,
        confidence: scoreConfidence(points, strictMode),
        why: ['Opening range breakout confirmed'],
        checklist: baseChecklist(rr >= 2, hasVolume),
        draw: [
          {
            kind: 'box',
            id: 'orb',
            fromIndex: sessionStartIndex,
            toIndex: windowEnd,
            high: orbHigh,
            low: orbLow,
            label: 'Opening Range'
          },
          { kind: 'line', id: 'entry', price: currClose, label: 'Entry' },
          { kind: 'line', id: 'stop', price: stopLevel, label: 'Stop' }
        ]
      }
    ];
  }
  if (currClose < orbLow) {
    const stopLevel = orbHigh;
    const targets = buildTargets(currClose, stopLevel, 'SHORT');
    const rr = computeRR(currClose, stopLevel, targets[0].level);
    const points = 40 + (rr >= 2 ? 20 : 10) + (hasVolume ? 10 : 0);
    return [
      {
        id: `orb-short-${candles[idx].t}`,
        symbol,
        timeframe,
        setup: 'ORB',
        direction: 'SHORT',
        status: 'TRIGGERED',
        detectedAt: candles[idx].t,
        patternStartAt: candles[Math.max(sessionStartIndex, 0)]?.t ?? candles[idx].t,
        entry: { type: 'MARKET_ON_TRIGGER', level: currClose, conditionText: 'Close below OR low' },
        stop: { level: stopLevel, reason: 'Back inside OR' },
        targets,
        confidence: scoreConfidence(points, strictMode),
        why: ['Opening range breakdown confirmed'],
        checklist: baseChecklist(rr >= 2, hasVolume),
        draw: [
          {
            kind: 'box',
            id: 'orb',
            fromIndex: sessionStartIndex,
            toIndex: windowEnd,
            high: orbHigh,
            low: orbLow,
            label: 'Opening Range'
          },
          { kind: 'line', id: 'entry', price: currClose, label: 'Entry' },
          { kind: 'line', id: 'stop', price: stopLevel, label: 'Stop' }
        ]
      }
    ];
  }
  return [];
};

export const detectFailedBreakout = (params: {
  symbol: string;
  timeframe: Timeframe;
  candles: Candle[];
  range: RangeState | null;
  strictMode: boolean;
  hasVolume: boolean;
}): DetectedSetup[] => {
  const { candles, range, symbol, timeframe, strictMode, hasVolume } = params;
  if (!range) return [];
  const idx = candles.length - 1;
  if (idx < 2) return [];
  const prev = candles[idx - 1];
  const curr = candles[idx];
  const setups: DetectedSetup[] = [];

  if (prev.c > range.high && curr.c < range.high) {
    const entryLevel = curr.c;
    const stopLevel = prev.h;
    const targets = [
      { level: (range.high + range.low) / 2, label: 'Midpoint' },
      { level: range.low, label: 'Opposite Range' }
    ];
    const rr = computeRR(entryLevel, stopLevel, targets[1].level);
    const points = 40 + (rr >= 2 ? 20 : 10) + (hasVolume ? 10 : 0);
    setups.push({
      id: `failed-breakout-short-${curr.t}`,
      symbol,
      timeframe,
      setup: 'FAILED_BREAKOUT',
      direction: 'SHORT',
      status: 'TRIGGERED',
      detectedAt: curr.t,
      patternStartAt: prev.t,
      entry: { type: 'LIMIT_RETEST', level: entryLevel, conditionText: 'Failed breakout back inside range' },
      stop: { level: stopLevel, reason: 'Re-acceptance above breakout' },
      targets,
      confidence: scoreConfidence(points, strictMode),
      why: ['Breakout failed within 1-2 candles', 'Price reclaimed range'],
      checklist: baseChecklist(rr >= 2, hasVolume),
      draw: [
        { kind: 'line', id: 'entry', price: entryLevel, label: 'Entry' },
        { kind: 'line', id: 'stop', price: stopLevel, label: 'Stop' }
      ]
    });
  }

  if (prev.c < range.low && curr.c > range.low) {
    const entryLevel = curr.c;
    const stopLevel = prev.l;
    const targets = [
      { level: (range.high + range.low) / 2, label: 'Midpoint' },
      { level: range.high, label: 'Opposite Range' }
    ];
    const rr = computeRR(entryLevel, stopLevel, targets[1].level);
    const points = 40 + (rr >= 2 ? 20 : 10) + (hasVolume ? 10 : 0);
    setups.push({
      id: `failed-breakout-long-${curr.t}`,
      symbol,
      timeframe,
      setup: 'FAILED_BREAKOUT',
      direction: 'LONG',
      status: 'TRIGGERED',
      detectedAt: curr.t,
      patternStartAt: prev.t,
      entry: { type: 'LIMIT_RETEST', level: entryLevel, conditionText: 'Failed breakdown back inside range' },
      stop: { level: stopLevel, reason: 'Re-acceptance below breakdown' },
      targets,
      confidence: scoreConfidence(points, strictMode),
      why: ['Breakdown failed within 1-2 candles', 'Price reclaimed range'],
      checklist: baseChecklist(rr >= 2, hasVolume),
      draw: [
        { kind: 'line', id: 'entry', price: entryLevel, label: 'Entry' },
        { kind: 'line', id: 'stop', price: stopLevel, label: 'Stop' }
      ]
    });
  }

  return setups;
};

export const detectMomentumReversal = (params: {
  symbol: string;
  timeframe: Timeframe;
  candles: Candle[];
  indicators: IndicatorSeries;
  strictMode: boolean;
  hasVolume: boolean;
}): DetectedSetup[] => {
  const { candles, indicators, symbol, timeframe, strictMode, hasVolume } = params;
  const idx = candles.length - 1;
  if (idx < 3) return [];
  const ema20 = indicators.ema20[idx];
  const atr = indicators.atr14[idx];
  if (!ema20 || !atr) return [];
  const curr = candles[idx];
  const prev = candles[idx - 1];
  const extension = Math.abs(curr.c - ema20) > atr * 2;
  const largeRange = (curr.h - curr.l) > atr * 1.5;
  if (!(extension && largeRange)) return [];
  const reversalLong = curr.c > prev.h;
  const reversalShort = curr.c < prev.l;
  if (!reversalLong && !reversalShort) return [];

  const direction = reversalLong ? 'LONG' : 'SHORT';
  const stopLevel = reversalLong ? curr.l : curr.h;
  const entryLevel = curr.c;
  const targets = buildTargets(entryLevel, stopLevel, direction);
  const rr = computeRR(entryLevel, stopLevel, targets[0].level);
  const points = 30 + (rr >= 2 ? 30 : 10) + (hasVolume ? 10 : 0);
  const extensionStartIndex = findExtensionStartIndex(candles, indicators, idx, 2);

  return [
    {
      id: `momentum-reversal-${direction.toLowerCase()}-${curr.t}`,
      symbol,
      timeframe,
      setup: 'EXHAUSTION_REVERSAL',
      direction,
      status: 'TRIGGERED',
      detectedAt: curr.t,
      patternStartAt: candles[extensionStartIndex]?.t ?? curr.t,
      entry: { type: 'MARKET_ON_TRIGGER', level: entryLevel, conditionText: 'Break of minor structure' },
      stop: { level: stopLevel, reason: 'Exhaustion extreme invalidation' },
      targets,
      confidence: scoreConfidence(points, strictMode),
      why: ['Extension beyond EMA20', 'Large range candle with reversal close'],
      checklist: baseChecklist(rr >= 2, hasVolume),
      draw: [
        { kind: 'line', id: 'entry', price: entryLevel, label: 'Entry' },
        { kind: 'line', id: 'stop', price: stopLevel, label: 'Stop' }
      ]
    }
  ];
};

export const detectFlag = (params: {
  symbol: string;
  timeframe: Timeframe;
  candles: Candle[];
  indicators: IndicatorSeries;
  strictMode: boolean;
  hasVolume: boolean;
}): DetectedSetup[] => {
  const { candles, indicators, symbol, timeframe, strictMode, hasVolume } = params;
  const idx = candles.length - 1;
  const atr = indicators.atr14[idx];
  if (!atr || idx < 12) return [];
  const impulseLookback = 8;
  const impulseStart = idx - impulseLookback;
  const impulseMove = candles[idx].c - candles[impulseStart].c;
  const impulseStrong = Math.abs(impulseMove) >= atr * 2;
  if (!impulseStrong) return [];
  const consolidationBars = 6;
  const consSlice = candles.slice(idx - consolidationBars, idx + 1);
  const consHigh = Math.max(...consSlice.map((c) => c.h));
  const consLow = Math.min(...consSlice.map((c) => c.l));
  const consRange = consHigh - consLow;
  if (consRange > atr * 1.2) return [];
  const direction = impulseMove > 0 ? 'LONG' : 'SHORT';
  const breakout = direction === 'LONG' ? candles[idx].c > consHigh : candles[idx].c < consLow;
  if (!breakout) return [];
  const entryLevel = candles[idx].c;
  const stopLevel = direction === 'LONG' ? consLow : consHigh;
  const targets = buildTargets(entryLevel, stopLevel, direction);
  const rr = computeRR(entryLevel, stopLevel, targets[0].level);
  const points = 30 + (rr >= 2 ? 30 : 10) + (hasVolume ? 10 : 0) + 15;

  const consolidationStartIndex = Math.max(idx - consolidationBars, 0);
  return [
    {
      id: `flag-${direction.toLowerCase()}-${candles[idx].t}`,
      symbol,
      timeframe,
      setup: 'FLAG',
      direction,
      status: 'TRIGGERED',
      detectedAt: candles[idx].t,
      patternStartAt: candles[consolidationStartIndex]?.t ?? candles[idx].t,
      entry: { type: 'MARKET_ON_TRIGGER', level: entryLevel, conditionText: 'Break of consolidation' },
      stop: { level: stopLevel, reason: 'Opposite side of flag' },
      targets,
      confidence: scoreConfidence(points, strictMode),
      why: ['Impulse move ≥ 2x ATR', 'Consolidation with compression'],
      checklist: baseChecklist(rr >= 2, hasVolume),
      draw: [
        { kind: 'box', id: 'flag', fromIndex: idx - consolidationBars, toIndex: idx, high: consHigh, low: consLow, label: 'Flag' },
        { kind: 'line', id: 'entry', price: entryLevel, label: 'Entry' },
        { kind: 'line', id: 'stop', price: stopLevel, label: 'Stop' }
      ]
    }
  ];
};
